import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const getSafeTotal = (total) => (total > 0 ? total : 1);

export default function useCarousel(totalItems, intervalMs = 3000) {
  const total = useMemo(() => getSafeTotal(totalItems), [totalItems]);
  const [position, setPosition] = useState(total > 1 ? 1 : 0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const [manualChangeVersion, setManualChangeVersion] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(() =>
    typeof document === 'undefined' ? true : !document.hidden
  );
  const resetRafRef = useRef(null);
  const enableRafRef = useRef(null);

  const markManualChange = useCallback(() => {
    setManualChangeVersion((current) => current + 1);
  }, []);

  useEffect(() => {
    setPosition(total > 1 ? 1 : 0);
    setIsTransitionEnabled(true);
  }, [total]);

  useEffect(
    () => () => {
      if (resetRafRef.current !== null) {
        cancelAnimationFrame(resetRafRef.current);
      }
      if (enableRafRef.current !== null) {
        cancelAnimationFrame(enableRafRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const activeIndex = useMemo(() => {
    if (total <= 1) {
      return 0;
    }

    return ((position - 1 + total) % total + total) % total;
  }, [position, total]);

  const jumpWithoutAnimation = useCallback((targetPosition) => {
    if (resetRafRef.current !== null) {
      cancelAnimationFrame(resetRafRef.current);
    }
    if (enableRafRef.current !== null) {
      cancelAnimationFrame(enableRafRef.current);
    }

    setIsTransitionEnabled(false);
    setPosition(targetPosition);
    resetRafRef.current = requestAnimationFrame(() => {
      enableRafRef.current = requestAnimationFrame(() => {
        setIsTransitionEnabled(true);
        resetRafRef.current = null;
        enableRafRef.current = null;
      });
    });
  }, []);

  const goToSlide = useCallback(
    (index) => {
      if (total <= 1) {
        return;
      }

      const nextIndex = ((index % total) + total) % total;
      markManualChange();
      setIsTransitionEnabled(true);
      setPosition(nextIndex + 1);
    },
    [markManualChange, total]
  );

  const goToNextInternal = useCallback(() => {
    if (total <= 1) {
      return;
    }

    setIsTransitionEnabled(true);
    setPosition((current) => (current >= total + 1 ? 2 : current + 1));
  }, [total]);

  const goToNext = useCallback(() => {
    if (total <= 1) {
      return;
    }

    markManualChange();
    goToNextInternal();
  }, [goToNextInternal, markManualChange, total]);

  const goToPrev = useCallback(() => {
    if (total <= 1) {
      return;
    }

    markManualChange();
    setIsTransitionEnabled(true);
    setPosition((current) => (current <= 0 ? total - 1 : current - 1));
  }, [markManualChange, total]);

  const onTransitionEnd = useCallback(() => {
    if (total <= 1) {
      return;
    }

    if (position === total + 1) {
      jumpWithoutAnimation(1);
      return;
    }

    if (position === 0) {
      jumpWithoutAnimation(total);
    }
  }, [jumpWithoutAnimation, position, total]);

  useEffect(() => {
    if (total <= 1) {
      return;
    }

    if (position > total + 1 || position < 0) {
      const normalizedPosition = ((position - 1 + total) % total) + 1;
      jumpWithoutAnimation(normalizedPosition);
    }
  }, [jumpWithoutAnimation, position, total]);

  useEffect(() => {
    if (!isPageVisible || total <= 1) {
      return;
    }

    if (position === total + 1) {
      jumpWithoutAnimation(1);
      return;
    }

    if (position === 0) {
      jumpWithoutAnimation(total);
    }
  }, [isPageVisible, jumpWithoutAnimation, position, total]);

  useEffect(() => {
    if (!isPageVisible || total <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      goToNextInternal();
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [goToNextInternal, intervalMs, isPageVisible, manualChangeVersion, total]);

  return {
    activeIndex,
    position,
    isTransitionEnabled,
    goToSlide,
    goToNext,
    goToPrev,
    onTransitionEnd,
  };
}
